import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '@src/environments/environment';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { MessageService } from 'primeng/api';
import { catchError, firstValueFrom, of, switchMap, take } from 'rxjs';
import { RefreshTokenResponse } from '../../types/refresh-token-response.type';
import { HeadlessService } from '../headless/headless.service';
import { LoadingService } from '../loading/loading.service';

@Injectable({
  providedIn: 'root',
})
export class AuthCustomService {
  constructor(
    private oidcSecurityService: OidcSecurityService,
    private messageService: MessageService,
    private router: Router,
    private translateService: TranslateService,
    private loadingService: LoadingService,
    private http: HttpClient,
    private headlessService: HeadlessService,
  ) {}

  public checkAuth(force = false): void {
    this.oidcSecurityService
      .checkAuthIncludingServer()
      .pipe(
        take(1),
        catchError((err) => {
          console.error('[AUTH] Error comprobando la sesión tras el login', err);
          this.goToLogin();
          return of(null);
        }),
      )
      .subscribe({
        next: (authenticated) => {
          if (!authenticated?.isAuthenticated || force) {
            this.oidcSecurityService
              .checkAuth()
              .pipe(
                take(1),
                catchError((err) => {
                  console.error('[AUTH] Error comprobando la sesión tras el login', err);
                  this.goToLogin();
                  return of(null);
                }),
              )
              .subscribe((res) => {
                if (!res || !res.isAuthenticated) {
                  this.sessionExpired();
                } else {
                  this.redirectAfterLogin();
                }
              });
          } else {
            this.redirectAfterLogin();
          }
        },
      });
  }

  public sessionExpired() {
    this.messageService.add({
      severity: 'warn',
      summary: this.translateService.instant('toast.sessionExpired.summary'),
      detail: this.translateService.instant('toast.sessionExpired.detail'),
      life: 10000,
    });
    this.goToLogin();
  }

  private goToLogin() {
    this.router.navigate(['login']);
    this.loadingService.hide();
  }

  //Gestión de redirección tras login
  private redirectAfterLogin(): void {
    // El guard usa esta flag para no re-invocar authorize() en bucle;
    // al confirmar sesión válida la limpiamos para permitir reintentos futuros.
    sessionStorage.removeItem('auth:autoAuthorizeAt');
    const redirectUrl = sessionStorage.getItem('redirectUrl');
    if (redirectUrl) {
      sessionStorage.removeItem('redirectUrl');
      this.router.navigateByUrl(redirectUrl);
    } else {
      this.router.navigate(['home']);
    }
  }

  public logout() {
    // Llamar al endpoint de Drupal de manera asíncrona
    this.headlessService
      .cerrarSesion()
      .pipe(
        take(1),
        catchError((err) => {
          console.error('[LOGOUT] Error cerrando sesión en Drupal:', err);
          return of(null);
        }),
      )
      .subscribe({
        complete: () => {
          // Cerrar sesión en Azure ADFS después de que Drupal responda
          this.oidcSecurityService
            .logoffAndRevokeTokens()
            .pipe(take(1))
            .subscribe();
        },
      });
  }

  async getTokenForEcoembesApi(): Promise<RefreshTokenResponse | null> {
    return firstValueFrom(
      this.requestTokenForScope(
        environment.declaracionAuth.scope,
        environment.declaracionAuth.resourceEndpoint,
      ),
    );
  }

  private requestTokenForScope(scope: string, resourceEndpoint?: string) {
    return this.oidcSecurityService.getRefreshToken().pipe(
      take(1),
      switchMap((refreshToken) => {
        if (refreshToken) {
          const authority = environment.ecoembesAuth.authority;
          const isV2Protocol = authority.endsWith('/v2.0');
          const url = isV2Protocol
            ? `${authority.slice(0, -'/v2.0'.length)}/oauth2/v2.0/token`
            : `${authority}/oauth2/token`;
          let body = new HttpParams()
            .set('client_id', environment.ecoembesAuth.clientId || '')
            .set('grant_type', 'refresh_token')
            .set('refresh_token', refreshToken);
          body = isV2Protocol
            ? body.set('scope', scope || '')
            : body.set('resource', resourceEndpoint || '');

          return this.http.post<RefreshTokenResponse>(url, body).pipe(
            take(1),
            catchError((err) => {
              console.error('Error obtaining token for Ecoembes API:', err);
              return of(null);
            }),
          );
        } else {
          return of(null);
        }
      }),
      catchError((err) => {
        console.error('Error obtaining token for Ecoembes API:', err);
        return of(null);
      }),
    );
  }
}
